/**
 * This utility is useful when dealing with returned values from the API that are loosely typed.
 * This is commonplace because the shortest way to define an array type in GraphQL is like this:
 *
 *  type Collection {
 *      items: [Item]  // both the `bars` array and its `Bar` elements are nullable
 *  }
 *
 * which translates to the following TypeScript type def:
 *
 *  type Collection = {
 *      items: Maybe<Maybe<Item>[]>; // our code generator uses Maybe<Item> to mean "null | Item"
 *  }
 *
 * What the API actually provides, in most cases, is either null or an array of items that are
 * non-nullable, but it's common for the schema to say that the items themselves are nullable.
 * To keep things type-safe, while reducing the amount of null checking we have to do, this function
 * takes one of these loosely defined arrays and removes the possibility that any item will be null.
 *
 * It's better to collaborate with whoever is writing the backend schema to make each element
 * non-nullable (defined in GraphQL with a `!`) but there are plenty of places where we failed to do
 * this for historical reasons, i.e. because we were in a hurry.
 *
 * @param items - either null or an array of values that are either null or of `ItemType`
 * @returns an array of only items of the ItemType
 */
export function removeNulls<Item>(
    items: undefined | Maybe<Maybe<Item>[]>,
): Item[] {
    if (items === undefined || items === null) {
        return [];
    }

    return items.reduce((acc: Item[], item: Maybe<Item>): Item[] => {
        if (item !== null) {
            acc.push(item);
        }

        return acc;
    }, [] as Item[]);
}
